import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { NavLink } from '@/components/NavLink';
import {
  LayoutDashboard, ScanSearch, Box, BarChart3, Wrench, ShieldCheck, Activity, CheckSquare,
  Cpu, Settings2, RefreshCcw, Brain, Layers, Factory, PackageCheck, Scissors,
  Bot, ClipboardList, Eye, QrCode, GitBranch, Gauge, Warehouse, FileText,
  Search, ScrollText, TrendingDown, Crosshair, Database, GitCompare, Workflow, Aperture, Shield, Video, Film,
  CreditCard, Bot as BotIcon, Bell, Package, Boxes, BarChart2, Radar, Siren, BugPlay,
  ChevronDown, ChevronsLeft, ChevronsRight, Star, Radio,
} from 'lucide-react';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarHeader, useSidebar,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { useIncidents } from '@/hooks/useIncidents';
import { useSidebarPrefs } from '@/hooks/useSidebarPrefs';
import { FavoritesSection } from '@/components/layout/sidebar-sections/FavoritesSection';
import { RecentsSection } from '@/components/layout/sidebar-sections/RecentsSection';
import { useModules } from '@/hooks/useModules';

interface SidebarItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * Main application sidebar (shadcn `Sidebar` collapsible variant).
 */
export function AppSidebar() {
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const { t } = useTranslation();
  const { incidents } = useIncidents();
  const activeIncidents = incidents.filter((i) => i.status !== 'resolved').length;
  const {
    favorites,
    recents,
    toggleFavorite,
    addRecent,
    isFavorite,
    collapsed: sectionCollapsed,
    setSectionCollapsed,
  } = useSidebarPrefs();
  const { isEnabled } = useModules();

  const frontOffice: { label: string; moduleKey: string; items: SidebarItem[] }[] = [
    {
      label: t('sidebar.produccion'),
      moduleKey: 'production',
      items: [
        { title: t('sidebar.dashboard'), url: '/', icon: LayoutDashboard },
        { title: t('sidebar.dispatchBoard'), url: '/production/dispatch', icon: ClipboardList },
        { title: t('sidebar.ensamblaje'), url: '/production/assembly', icon: PackageCheck },
        { title: t('sidebar.corteGuiado'), url: '/production/cutting', icon: Scissors },
        { title: t('sidebar.cobotica'), url: '/production/cobotics', icon: Bot },
        { title: t('sidebar.wipBoard'), url: '/production/wip', icon: Layers },
      ],
    },
    {
      label: t('sidebar.calidad'),
      moduleKey: 'quality',
      items: [
        { title: t('sidebar.qualityBoard'), url: '/quality/board', icon: ShieldCheck },
        { title: t('sidebar.deteccionDefectos'), url: '/quality/defect-detection', icon: ScanSearch },
        { title: t('sidebar.vision3d'), url: '/quality/vision-3d', icon: Eye },
        { title: t('sidebar.empaquetado'), url: '/quality/packaging', icon: Box },
        { title: t('sidebar.analyticsQA'), url: '/quality/analytics', icon: BarChart3 },
        { title: t('sidebar.calibracionCamara'), url: '/quality/calibration', icon: Aperture },
      ],
    },
    {
      label: t('sidebar.logistica'),
      moduleKey: 'logistics',
      items: [
        { title: t('sidebar.almacen'), url: '/logistics/warehouse', icon: Warehouse },
        { title: t('sidebar.trazabilidad'), url: '/traceability/search', icon: Search },
        { title: t('sidebar.inventario'), url: '/materials/inventory', icon: Box },
        { title: t('sidebar.codigoBarras'), url: '/materials/barcode', icon: QrCode },
      ],
    },
    {
      label: t('sidebar.mantenimiento'),
      moduleKey: 'maintenance',
      items: [
        { title: t('sidebar.predictivo'), url: '/maintenance/predictive', icon: Activity },
        { title: t('sidebar.condicion'), url: '/maintenance/condition', icon: Gauge },
        { title: t('sidebar.seguridad'), url: '/maintenance/safety', icon: ShieldCheck },
      ],
    },
    {
      label: t('sidebar.reportes'),
      moduleKey: 'reports',
      items: [
        { title: t('sidebar.controlTower'), url: '/analytics/reports', icon: BarChart3 },
        { title: t('sidebar.auditLog'), url: '/system/audit-log', icon: ScrollText },
        { title: t('sidebar.dossierFAI'), url: '/fai/dossier', icon: FileText },
      ],
    },
  ];

  const backOffice: SidebarItem[] = [
    { title: t('sidebar.dispositivos'), url: '/backoffice/devices', icon: Cpu },
    { title: t('sidebar.motorReglas'), url: '/backoffice/rules', icon: Settings2 },
    { title: t('sidebar.syncERP'), url: '/backoffice/erp-sync', icon: RefreshCcw },
    { title: t('sidebar.adminPanel'), url: '/backoffice/admin', icon: Shield },
    { title: t('sidebar.members', 'Miembros'), url: '/settings/members', icon: Shield },
    { title: t('sidebar.facturacion', 'Facturación'), url: '/backoffice/billing', icon: CreditCard },
    { title: t('sidebar.copilot', 'Copilot'), url: '/copilot', icon: BotIcon },
    { title: t('sidebar.alertas', 'Alertas'), url: '/alerts', icon: Bell },
    { title: t('sidebar.storage', 'Storage'), url: '/storage', icon: Package },
    { title: t('sidebar.cache', 'Cache'), url: '/cache', icon: Boxes },
    { title: t('sidebar.analytics', 'Analytics'), url: '/analytics', icon: BarChart2 },
    { title: t('sidebar.apm', 'APM'), url: '/apm', icon: Radar },
    { title: t('sidebar.incidents', 'Incidentes'), url: '/incidents', icon: Siren },
    { title: t('sidebar.errors', 'Errores'), url: '/errors', icon: BugPlay },
  ];

  const mlops: SidebarItem[] = [
    { title: t('sidebar.liveInference', 'Inferencia en vivo'), url: '/mlops/live-inference', icon: Radio },
    { title: t('sidebar.fabricaModelos'), url: '/mlops/model-factory', icon: Wrench },
    { title: t('sidebar.videoInference', 'Inferencia sobre vídeo'), url: '/mlops/video-inference', icon: Film },
    { title: t('sidebar.anotacionSAM2'), url: '/mlops/sam2', icon: Brain },
    { title: t('sidebar.closedLoopHealth', 'Salud del ciclo'), url: '/mlops/closed-loop', icon: Activity },
    { title: t('sidebar.inferenceFeedback', 'Feedback inferencias'), url: '/mlops/feedback', icon: CheckSquare },
    { title: t('sidebar.driftMonitoring'), url: '/mlops/drift', icon: TrendingDown },
    { title: t('sidebar.zeroShot'), url: '/mlops/zero-shot', icon: Crosshair },
    { title: t('sidebar.datasetQA'), url: '/mlops/dataset-qa', icon: Database },
    { title: t('sidebar.vectorSearch'), url: '/mlops/vector-search', icon: Search },
    { title: t('sidebar.experimentos'), url: '/mlops/experiments', icon: GitCompare },
    { title: t('sidebar.pipelineONNX'), url: '/mlops/onnx-pipeline', icon: Workflow },
    { title: t('sidebar.visionIndustrial', 'Visión Industrial'), url: '/mlops/vision-industrial', icon: Video },
  ];

  const omniverse: SidebarItem[] = [
    { title: t('sidebar.omniverseHub', 'Hub Omniverse'), url: '/omniverse', icon: Box },
    { title: t('sidebar.omniverseConvert', 'Convertir CAD'), url: '/omniverse/convert', icon: Layers },
    { title: t('sidebar.omniverseGenerate', 'Generar Dataset'), url: '/omniverse/generate', icon: Database },
    { title: t('sidebar.omniverseDatasets', 'Datasets GCS'), url: '/omniverse/datasets', icon: Package },
  ];

  // Flat lookup of all known sidebar entries (path -> title)
  const pathTitleMap = useMemo(() => {
    const map = new Map<string, string>();
    frontOffice.forEach((g) => g.items.forEach((i) => map.set(i.url, i.title)));
    backOffice.forEach((i) => map.set(i.url, i.title));
    mlops.forEach((i) => map.set(i.url, i.title));
    omniverse.forEach((i) => map.set(i.url, i.title));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const getTitle = (path: string) => pathTitleMap.get(path) ?? path;

  // Track recents whenever the pathname matches a known sidebar route.
  useEffect(() => {
    if (pathTitleMap.has(location.pathname)) {
      addRecent(location.pathname);
    }
  }, [location.pathname, pathTitleMap, addRecent]);

  const isActive = (url: string) => location.pathname === url;

  const renderItem = (item: SidebarItem, opts?: { showBadge?: boolean }) => {
    const fav = isFavorite(item.url);
    return (
      <SidebarMenuItem key={item.url} className="group/item relative">
        <SidebarMenuButton asChild>
          <NavLink
            to={item.url}
            end={item.url === '/'}
            className={cn(
              'flex items-center gap-2 rounded-md px-2 py-1.5 pr-8 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors',
              isActive(item.url) && 'bg-sidebar-accent text-sidebar-primary font-medium',
            )}
            activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
          >
            <item.icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="flex-1 truncate">{item.title}</span>}
            {!collapsed && opts?.showBadge && activeIncidents > 0 && (
              <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-[10px]">
                {activeIncidents}
              </Badge>
            )}
          </NavLink>
        </SidebarMenuButton>
        {!collapsed && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleFavorite(item.url);
            }}
            className={cn(
              'absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-md',
              'text-sidebar-foreground/40 hover:text-yellow-400 hover:bg-sidebar-accent transition-opacity',
              fav ? 'opacity-100 text-yellow-400' : 'opacity-0 group-hover/item:opacity-100 focus-visible:opacity-100',
            )}
            aria-label={fav ? `Quitar ${item.title} de favoritos` : `Añadir ${item.title} a favoritos`}
            title={fav ? 'Quitar de favoritos' : 'Añadir a favoritos'}
          >
            <Star className={cn('h-3.5 w-3.5', fav && 'fill-yellow-400')} />
          </button>
        )}
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarHeader className="border-b border-sidebar-border px-3 py-3">
        <div className={cn('flex items-center', collapsed ? 'flex-col gap-2' : 'justify-between gap-2')}>
          {!collapsed ? (
            <div className="flex items-center gap-2 min-w-0">
              <Factory className="h-6 w-6 text-sidebar-primary shrink-0" />
              <div className="min-w-0">
                <h1 className="text-sm font-bold tracking-wide text-sidebar-foreground truncate">EXPAI</h1>
                <p className="text-[10px] uppercase tracking-widest text-sidebar-muted truncate">SmartIndustry MES</p>
              </div>
            </div>
          ) : (
            <Factory className="h-6 w-6 text-sidebar-primary" />
          )}
          <button
            type="button"
            onClick={toggleSidebar}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors shrink-0"
            aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            title={collapsed ? 'Expandir' : 'Colapsar'}
          >
            {collapsed ? <ChevronsRight className="h-4 w-4" /> : <ChevronsLeft className="h-4 w-4" />}
          </button>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-2">
        {/* FAVORITES */}
        <FavoritesSection
          favorites={favorites}
          collapsed={sectionCollapsed.favorites}
          sidebarCollapsed={collapsed}
          onOpenChange={(open) => setSectionCollapsed('favorites', !open)}
          getTitle={getTitle}
          isActive={isActive}
        />

        {/* RECENTS */}
        <RecentsSection
          recents={recents}
          collapsed={sectionCollapsed.recents}
          sidebarCollapsed={collapsed}
          onOpenChange={(open) => setSectionCollapsed('recents', !open)}
          getTitle={getTitle}
          isActive={isActive}
        />

        {/* FRONT OFFICE */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] uppercase tracking-widest text-sidebar-muted px-2">
            {!collapsed && t('sidebar.frontOffice')}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            {frontOffice.filter((g) => isEnabled(g.moduleKey)).map((group) => (
              <Collapsible key={group.label} defaultOpen={group.items.some((i) => isActive(i.url))}>
                <CollapsibleTrigger className={cn(
                  'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-sidebar-muted hover:bg-sidebar-accent transition-colors',
                  collapsed && 'justify-center'
                )}>
                  {!collapsed && <span>{group.label}</span>}
                  {!collapsed && <ChevronDown className="h-3 w-3" />}
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenu>
                    {group.items.map((item) => renderItem(item))}
                  </SidebarMenu>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </SidebarGroupContent>
        </SidebarGroup>

        {/* BACK OFFICE */}
        {isEnabled('backoffice') && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-widest text-sidebar-muted px-2">
              {!collapsed && t('sidebar.backOffice')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {backOffice.map((item) => renderItem(item, { showBadge: item.url === '/incidents' }))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* MLOPS */}
        {isEnabled('mlops') && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-widest text-sidebar-muted px-2">
              {!collapsed && t('sidebar.mlops')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {mlops.map((item) => renderItem(item))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* OMNIVERSE */}
        {isEnabled('mlops') && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-widest text-sidebar-muted px-2">
              {!collapsed && t('sidebar.omniverse', '🔷 Omniverse')}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {omniverse.map((item) => renderItem(item))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
}
